import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  reviewApi,
  type ReviewQueueItemView,
} from '../../api/endpoints.js';
import {
  ActionButton,
  Alert,
  DataTable,
  PageHeader,
  Panel,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { WorkCell } from './work-ui.js';

/**
 * THE TECHNICAL REVIEW QUEUE — module C7.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 5 AND 8 BEFORE ADDING ANYTHING TO THIS PAGE.
 * ===========================================================================
 *
 * WHAT REVIEW IS. A second person reading a RECORD: what was written, when the
 * work was done, which instruments were used, what was attached. It is not a
 * judgement of the material — there is no conformity, pass, fail or limit on
 * this page, for any test.
 *
 * THE CALLER'S OWN WORK IS SHOWN, FLAGGED AND REFUSED (decision D108). Hiding
 * it would leave a technician wondering where their submission went; offering
 * the button would promise something the server refuses. So the row is here,
 * marked as theirs, with the action disabled — and the API and a database
 * trigger both refuse it anyway if anybody tries.
 */
export function ReviewQueuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  const [page, setPage] = useState(1);

  const queue = useQuery({
    queryKey: ['review-queue', { page }],
    queryFn: () => reviewApi.queue({ page }),
  });

  const columns: readonly DataColumn<ReviewQueueItemView>[] = [
    {
      key: 'work',
      header: t('work.workColumn'),
      render: (row) => <WorkCell assignment={row.assignment} to={`/work/${row.assignmentId}`} />,
    },
    {
      key: 'performedBy',
      header: t('review.performedByColumn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-700">{row.performedBy.displayName}</span>
          {row.callerIsPerformer ? (
            <StatusBadge label={t('review.ownWork')} tone="warning" />
          ) : null}
        </div>
      ),
    },
    {
      key: 'submitted',
      header: t('review.submittedColumn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-700">
            {formatDateTime(row.submittedAt, { locale }) ?? row.submittedAt}
          </span>
          <span className="block text-caption text-default-600">
            {t('review.revisionNumber', { number: row.revisionNumber })}
            {row.isRetest ? ` · ${t('review.retestFlag')}` : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'returns',
      header: t('review.returnsColumn'),
      render: (row) => (
        <span className="text-default-700 tabular-nums" dir="ltr">
          {row.returnCount}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton
          priority={row.callerIsPerformer ? 'tertiary' : 'secondary'}
          size="sm"
          // Disabled because the server refuses it: a person never reviews
          // their own work (D107). The refusal lives in the API and in a
          // database trigger; this only avoids offering a dead end.
          isDisabled={row.callerIsPerformer}
          onPress={() => navigate(`/review/${row.attemptId}`)}
        >
          {t('review.reviewAction')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('review.title')}
        subtitle={t('review.subtitle')}
        breadcrumbs={[{ label: t('nav.review') }]}
      />

      <div className="mb-gutter">
        <Alert
          tone="info"
          title={t('review.recordOnlyTitle')}
          description={t('review.recordOnlyBody')}
        />
      </div>

      <Panel>
        <DataTable
          label={t('review.title')}
          columns={columns}
          rows={queue.data?.data ?? []}
          rowKey={(row) => row.attemptId}
          isLoading={queue.isLoading}
          error={queue.error}
          onRetry={() => void queue.refetch()}
          emptyContent={t('review.none')}
          page={page}
          totalPages={queue.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
